import { createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { Box, HStack, Text, type BoxProps } from "@chakra-ui/react"

/*
 * A card's name with its chips beside it, lined up across a list of cards: every card's
 * chips start the same distance after the list's longest name, with a dotted line from
 * each name to its chips. Wrap the list in AlignedTitles; each card uses TitleWithChips.
 */

// Past this a name is cut short, and shown whole on hover
const TITLE_MAX = "24rem"
// From the longest name to the chips, filled with dots
const TITLE_GAP = "2rem"
// The fewest dots after a name, the longest or a cut-short one included
const LEADER_MIN = "1.25rem"
// Room for a name input (a card being edited), on every card so view and edit line up
const SLOT_MIN = "14rem"

const TITLE_TEXT = "data-aligned-title-text"
/** Put on a name input, so a card being edited still counts towards the width */
export const ALIGNED_TITLE_INPUT = "data-aligned-title-input"

const AlignedWidth = createContext<{ width?: string; gap: string }>({ gap: TITLE_GAP })

/**
 * Lines up the titles of the cards inside it, to the longest of them. Measured in the
 * font on screen once it has loaded, and again whenever a name changes.
 */
export function AlignedTitles({
  children,
  gap = TITLE_GAP,
  ...props
}: {
  children: ReactNode
  /** From the longest name to the chips, filled with dots */
  gap?: string
} & Omit<BoxProps, "gap">) {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<string>()

  useLayoutEffect(() => {
    const root = ref.current
    if (!root) return
    let live = true
    const measure = () => {
      const texts = Array.from(root.querySelectorAll<HTMLElement>(`[${TITLE_TEXT}]`))
      const inputs = Array.from(root.querySelectorAll<HTMLInputElement>(`[${ALIGNED_TITLE_INPUT}]`))
      const sample = texts[0]
      const context = sample && document.createElement("canvas").getContext("2d")
      if (!live || !sample || !context) return
      context.font = getComputedStyle(sample).font
      const names = [...texts.map((t) => t.textContent ?? ""), ...inputs.map((i) => i.value)]
      const widest = Math.max(0, ...names.map((name) => context.measureText(name).width))
      const next = widest > 0 ? `min(${Math.ceil(widest)}px, ${TITLE_MAX})` : undefined
      setWidth((current) => (current === next ? current : next))
    }
    measure()
    document.fonts?.ready.then(measure)
    // Names change, cards come and go (expanding a list, editing a name)
    const observer = new MutationObserver(measure)
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["value"] })
    root.addEventListener("input", measure)
    return () => {
      live = false
      observer.disconnect()
      root.removeEventListener("input", measure)
    }
  }, [])

  return (
    <Box ref={ref} {...props}>
      <AlignedWidth.Provider value={{ width, gap }}>{children}</AlignedWidth.Provider>
    </Box>
  )
}

/**
 * A name (or its input, while editing) and its chips. Inside AlignedTitles the chips line
 * up with the other cards'; on its own the name just sits before them.
 */
export function TitleWithChips({
  title,
  chips,
  colour,
}: {
  title: ReactNode
  chips?: ReactNode
  colour?: string
}) {
  const { width, gap } = useContext(AlignedWidth)
  return (
    <HStack columnGap={2} rowGap={2} align="center" flexWrap="wrap" minW={0}>
      <Box
        w={width && `max(calc(${width} + ${gap}), ${SLOT_MIN})`}
        // A name input always gets room, measured or not
        minW={width || typeof title !== "string" ? SLOT_MIN : undefined}
        maxW="100%"
        flexShrink={0}
      >
        {typeof title === "string" ? (
          <HStack gap={2} align="center" minW={0} title={title}>
            {/* A longer name is cut short, and shown whole on hover */}
            <Text fontWeight="semibold" fontSize="md" color={colour} truncate minW={0} maxW={TITLE_MAX}>
              <span {...{ [TITLE_TEXT]: "" }}>{title}</span>
            </Text>
            {/* Dots from the end of the name to the chips */}
            {chips && (
              <Box aria-hidden flex={1} minW={LEADER_MIN} alignSelf="center" mt="3px" borderBottom="2px dotted #C4C4C4" />
            )}
          </HStack>
        ) : (
          title
        )}
      </Box>
      {chips && (
        <HStack gap={2} align="center" flexWrap="wrap">
          {chips}
        </HStack>
      )}
    </HStack>
  )
}

export default TitleWithChips
